import {
  call,
  takeLatest,
  takeEvery,
  put,
  CallEffect,
  PutEffect,
  ForkEffect,
} from "redux-saga/effects";
import { actions } from "reducers/rainModal";
import { actions as regionActions } from "reducers/regions";
import * as regionsMiddleware from "middleware/regions";
import { AxiosResponse } from "axios";
import { PayloadAction } from "@reduxjs/toolkit";
import {
  RainCreationParamsType,
  RainsFilters,
  RainUpdateParamsType,
} from "reducers/types/rainModal";
import {
  RainCreationResponseType,
  RainsResponseType,
  RainUpdateRequestType,
  RainValidationRequestType,
  RainValidationResponseType,
} from "./types/rainModal";
import { ApiResponse } from "middleware/api";
import { DATE_FORMATS, RAIN_TYPES } from "utils/constants";
import moment from "moment";
import { store } from "store";

const sagas: ForkEffect<never>[] = [
  takeLatest(actions.getRainsFetch.type, getRains),
  takeLatest(actions.addRainFetch.type, addRain),
  takeLatest(actions.deleteRainFetch.type, deleteRain),
  takeLatest(actions.updateRainFetch.type, updateRain),
  takeEvery(actions.validateRainTimeCreationFetch.type, validateRainCreation),
  takeEvery(actions.validateRainTimeUpdateFetch.type, validateRainUpdate),
];

export default sagas;

function* refreshRains() {
  const { filter, dropdownState, order } = store.getState().rainModal;
  const { actualRegion } = store.getState().regions;
  yield put(
    actions.getRainsFetch({
      params: {
        ...filter,
        order: order[dropdownState],
        type: RAIN_TYPES[dropdownState],
      },
      regionId: actualRegion?.id || "",
    })
  );
}

function* getRains({
  payload,
}: PayloadAction<RainsFilters>): Generator<
  CallEffect<AxiosResponse<RainsResponseType>> | PutEffect<{ type: string }>,
  void,
  ApiResponse<RainsResponseType>
> {
  const response = yield call(
    regionsMiddleware.getRains,
    payload.params,
    payload.regionId
  );
  if (response.status !== 200) {
    yield put(actions.getRainsError());
    yield put(actions.resetBlockedTab());
  } else {
    const { result } = response.data;
    yield put(actions.getRainsSuccess(result.items));
    yield put(actions.resetBlockedTab());
  }
}

function* addRain({
  payload,
}: PayloadAction<RainCreationParamsType>): Generator<
  | CallEffect<AxiosResponse<RainCreationResponseType>>
  | PutEffect<{ type: string }>,
  void,
  ApiResponse<RainCreationResponseType>
> {
  const formattedParams = {
    ...payload.requestParams,
    date: moment(payload.requestParams.date, DATE_FORMATS.shortDate).format(
      DATE_FORMATS.shortISODate
    ),
    startAt: `${payload.requestParams.startAt}:00`,
    finishAt: `${payload.requestParams.finishAt}:00`,
  };
  const response = yield call(regionsMiddleware.addRain, formattedParams);
  if (response.status !== 200) {
    yield put(actions.addRainError());
    payload.callback(
      payload.requestParams.date,
      payload.requestParams.finishAt
    );
  } else {
    payload.callback(
      payload.requestParams.date,
      payload.requestParams.finishAt
    );
    store.dispatch(
      regionActions.getNextRainRequest(
        store.getState().regions.actualRegion?.id || ""
      )
    );
    yield put(actions.addRainSuccess());
  }
}

function* deleteRain({
  payload,
}: PayloadAction<string>): Generator<
  | CallEffect<AxiosResponse<RainCreationResponseType>>
  | ReturnType<typeof refreshRains>
  | PutEffect<{ type: string }>,
  void,
  ApiResponse<RainCreationResponseType>
> {
  const response = yield call(regionsMiddleware.deleteRain, payload);
  if (response.status !== 200) {
    yield refreshRains();
    yield put(actions.deleteRainError());
  } else {
    yield refreshRains();
    store.dispatch(
      regionActions.getNextRainRequest(
        store.getState().regions.actualRegion?.id || ""
      )
    );
    yield put(actions.deleteRainSuccess());
  }
}

function* updateRain({
  payload,
}: PayloadAction<RainUpdateParamsType>): Generator<
  | CallEffect<AxiosResponse<RainCreationResponseType>>
  | ReturnType<typeof refreshRains>
  | PutEffect<{ type: string }>,
  void,
  ApiResponse<RainCreationResponseType>
> {
  const params: RainUpdateRequestType = {
    startAt: `${payload.requestParams.startAt}:00`,
    finishAt: `${payload.requestParams.finishAt}:00`,
  };
  const response = yield call(
    regionsMiddleware.updateRain,
    params,
    payload.rainId
  );
  if (response.status !== 200) {
    yield refreshRains();
    yield put(actions.updateRainError());
  } else {
    yield refreshRains();
    store.dispatch(
      regionActions.getNextRainRequest(
        store.getState().regions.actualRegion?.id || ""
      )
    );
    yield put(actions.updateRainSuccess());
  }
}

function* validateRainCreation({
  payload,
}: PayloadAction<RainValidationRequestType>): Generator<
  | CallEffect<AxiosResponse<ApiResponse<RainValidationResponseType>>>
  | PutEffect<{ type: string }>,
  void,
  ApiResponse<RainValidationResponseType>
> {
  let formattedParams = {
    ...payload,
    date: moment(payload.date, DATE_FORMATS.shortDate).format(
      DATE_FORMATS.shortISODate
    ),
    startAt: payload.startAt ? `${payload.startAt}:00` : undefined,
    finishAt: payload.finishAt ? `${payload.finishAt}:00` : undefined,
  };

  const response = yield call(regionsMiddleware.validateRain, formattedParams);
  if (response.status !== 200) {
    yield put(
      actions.validateRainTimeCreationError(response.data.errors as string[])
    );
  } else {
    yield put(actions.validateRainTimeCreationSuccess());
  }
}

function* validateRainUpdate({
  payload,
}: PayloadAction<RainValidationRequestType>): Generator<
  | CallEffect<AxiosResponse<ApiResponse<RainValidationResponseType>>>
  | PutEffect<{ type: string }>,
  void,
  ApiResponse<RainValidationResponseType>
> {
  const formattedParams = {
    ...payload,
    date: moment(payload.date, DATE_FORMATS.shortDate).format(
      DATE_FORMATS.shortISODate
    ),
    startAt: payload.startAt ? `${payload.startAt}:00` : undefined,
    finishAt: payload.finishAt ? `${payload.finishAt}:00` : undefined,
  };
  const response = yield call(regionsMiddleware.validateRain, formattedParams);
  if (response.status !== 200) {
    yield put(
      actions.validateRainTimeUpdateError(response.data.errors as string[])
    );
  } else {
    yield put(actions.validateRainTimeUpdateSuccess());
  }
}
